'use client';

import { forwardRef, SVGProps } from 'react';

/**
 * PISRS Logo Icon (symbol only, without text)
 * Based on the official PISRS logo guidelines
 *
 * The symbol represents:
 * - Three books tilted at 45° forming the shape of Triglav mountain
 * - Three horizontal lines turning vertical with circles (electronic circuit)
 *
 * Primary color: #1B495A (dark teal)
 */
export const PisrsIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        {/* Mountain/Books - three peaks representing Triglav */}
        <path
          d="M4 14 L8 8 L12 14"
          fill="currentColor"
          stroke="none"
        />
        <path
          d="M7 14 L12 6 L17 14"
          fill="currentColor"
          stroke="none"
        />
        <path
          d="M12 14 L16 9 L20 14"
          fill="currentColor"
          stroke="none"
        />

        {/* Circuit lines - three horizontal lines with vertical branch */}
        <line x1="4" y1="15.5" x2="20" y2="15.5" strokeWidth="1.2" />
        <line x1="4" y1="17.5" x2="20" y2="17.5" strokeWidth="1.2" />
        <line x1="4" y1="19.5" x2="20" y2="19.5" strokeWidth="1.2" />

        {/* Vertical branch with circles (circuit nodes) */}
        <line x1="6" y1="15.5" x2="6" y2="21" strokeWidth="1.2" />
        <line x1="4" y1="17.5" x2="4" y2="22" strokeWidth="1.2" />
        <circle cx="6" cy="21.5" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="4" cy="22.5" r="0.8" fill="currentColor" stroke="none" />
      </svg>
    );
  }
);

PisrsIcon.displayName = 'PisrsIcon';

/**
 * Alternative simpler version for very small sizes
 */
export const PisrsIconSimple = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ className, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        {...props}
      >
        {/* Simplified mountain shape */}
        <path d="M2 16 L6 10 L10 16 Z" />
        <path d="M6 16 L12 6 L18 16 Z" />
        <path d="M14 16 L18 10 L22 16 Z" />

        {/* Three lines */}
        <rect x="2" y="17" width="20" height="1.5" rx="0.5" />
        <rect x="2" y="19.5" width="20" height="1.5" rx="0.5" />
        <rect x="2" y="22" width="16" height="1.5" rx="0.5" />
      </svg>
    );
  }
);

PisrsIconSimple.displayName = 'PisrsIconSimple';
